import React from 'react';
import {addCommasToNumber} from '../../../utils';

/**
 * Table building blocks. They render the same markup as the multi-tenant
 * BaseTabComponent (td.value-cell / td.editable-cell / label.formatted-number-label),
 * so the Financials.scss styling applies unchanged: blue cells are inputs, white cells are
 * calculated.
 */

/** A titled white card - .Financials_table_container with its h3. */
export function Section({title, description, className = '', actions, children}) {
    return (
        <div className={`Financials_table_container ${className}`}>
            {title ?
                <h3 className='Financials_table_title'>
                    {title}
                    {description ? <span className='Financials_table_description'>{description}</span> : null}
                </h3>
                : null}
            {children}
            {actions ? <div className='mf-section-actions'>{actions}</div> : null}
        </div>
    );
}

export function Table({className = '', header, children, footer}) {
    return (
        <table className={`Financials_table ${className}`}>
            <tbody>
            {header ?
                <tr>{header.map((label, index) => (label === null
                    // null marks the narrow column holding each row's remove button.
                    ? <td className='mf-remove-cell' key={index}/>
                    : <td className='header-cell' key={index}><div className='text'>{label}</div></td>
                ))}</tr>
                : null}
            {children}
            {footer}
            </tbody>
        </table>
    );
}

export function NameCell({children, className = ''}) {
    return (
        <td className={`name-cell ${className}`}>
            <span className='text' title={typeof children === 'string' ? children : undefined}>{children}</span>
        </td>
    );
}

/** A calculated (white) cell. `note` prints muted on the left, e.g. the rate a line is based on. */
export function ValueCell({children, note, className = ''}) {
    return (
        <td className={`value-cell ${children === '-' ? 'center-dashes' : ''} ${className}`}>
            {note ? <span className='mf-note'>{note}</span> : null}
            <span className='text value'>{children}</span>
        </td>
    );
}

function display(value, unit, decimals) {
    if (value === '' || value === null || value === undefined) return '';
    const n = Number(value);
    if (!isFinite(n)) return value;
    const shown = decimals ? n.toLocaleString('en-US', {maximumFractionDigits: decimals}) : addCommasToNumber(n);
    if (unit === '$') return `$${shown}`;
    if (unit === '%') return `${shown}%`;
    return unit ? `${shown} ${unit}` : shown;
}

/**
 * A manual-input (blue) cell.
 *
 *   kind 'number' - shows the formatted value until focused, then the raw number;
 *                   `unit` is '$', '%', or a suffix such as 'SF', 'Years', 'Acres'.
 *   kind 'text' | 'date' | 'link' | 'select' (with `options` [{value, label}]).
 */
export function InputCell({value, onChange, kind = 'number', unit, decimals, options, placeholder, className = ''}) {
    const handle = event => onChange(event.currentTarget.value);
    const safeValue = value === null || value === undefined ? '' : value;

    if (kind === 'select') {
        return (
            <td className={`value-cell editable-cell ${className}`}>
                <label>
                    <select value={safeValue} onChange={handle}>
                        <option value=''/>
                        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </label>
            </td>
        );
    }

    if (kind === 'number') {
        return (
            <td className={`value-cell editable-cell ${className}`}>
                <label className='number formatted-number-label'>
                    <input type='number' step='any' value={safeValue} onChange={handle} placeholder={unit}/>
                    <div className='input-display-value'>{display(safeValue, unit, decimals)}</div>
                </label>
            </td>
        );
    }

    return (
        <td className={`value-cell editable-cell mf-text-cell ${className}`}>
            <label className={kind === 'link' ? 'link' : `mf-${kind}-label`}>
                <input
                    type={kind === 'date' ? 'date' : 'text'}
                    value={safeValue}
                    onChange={handle}
                    placeholder={placeholder}
                />
                {kind === 'link' && safeValue ?
                    <a className='link-button' href={safeValue} target='_blank' rel='noopener noreferrer' title='Open link'>
                        <svg aria-hidden='true' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'>
                            <path fill='currentColor' d='M576 24v127.984c0 21.461-25.96 31.98-40.971 16.971l-35.707-35.709-243.523 243.523c-9.373 9.373-24.568 9.373-33.941 0l-22.627-22.627c-9.373-9.373-9.373-24.569 0-33.941L442.756 76.676l-35.703-35.705C391.982 25.9 402.656 0 424.024 0H552c13.255 0 24 10.745 24 24zM407.029 270.794l-16 16A23.999 23.999 0 0 0 384 303.765V448H64V128h264a24.003 24.003 0 0 0 16.97-7.029l16-16C376.089 89.851 365.381 64 344 64H48C21.49 64 0 85.49 0 112v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V287.764c0-21.382-25.852-32.09-40.971-16.97z'/>
                        </svg>
                    </a>
                    : null}
            </label>
        </td>
    );
}

/** The blank-cell used where the workbook has no figure (e.g. Proforma Per Unit on totals). */
export function EmptyCell() {
    return <td className='value-cell mf-empty-cell'/>;
}

export function AddRowButton({label = 'Add Row', onClick}) {
    return <button type='button' className='button-new positive mf-add-row' onClick={onClick}>+ {label}</button>;
}

/** The ✕ at the end of a removable row. */
export function RemoveCell({onRemove, disabled}) {
    return (
        <td className='mf-remove-cell'>
            {disabled ? null :
                <button type='button' className='mf-remove-button' onClick={onRemove} title='Remove row' aria-label='Remove row'>×</button>}
        </td>
    );
}
